import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, ShieldCheck } from 'lucide-react';
import { useAuth } from './AuthContext';
import { AUTH, AuthCardShell, AuthSigningInScreen } from './AuthCard';
import { ROUTES } from '../../config/links';

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, isInitialized, error, login, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || ROUTES.ROOT;
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleLogin = async () => {
    clearError();
    await login();
  };

  if (!isInitialized || isLoading) {
    return <AuthSigningInScreen />;
  }

  return (
    <AuthCardShell title="CalibraOne" subtitle="Sign in to access your dashboard">
      <div
        className="flex items-center justify-center"
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          border: `2px solid ${AUTH.iconRing}`,
          backgroundColor: AUTH.iconBg,
          boxShadow: AUTH.iconGlow,
          color: AUTH.iconColor,
        }}
      >
        <ShieldCheck size={40} strokeWidth={1.75} />
      </div>

      {error && (
        <div
          className="w-full text-left text-sm"
          style={{
            padding: 16,
            borderRadius: 10,
            backgroundColor: AUTH.errorBg,
            border: `1px solid ${AUTH.errorBorder}`,
            color: AUTH.errorText,
          }}
        >
          <p className="font-medium" style={{ color: 'rgba(255,255,255,0.9)', margin: 0 }}>
            Authentication Error
          </p>
          <p style={{ margin: '4px 0 0' }}>{error}</p>
        </div>
      )}

      <div className="w-full">
        <button
          type="button"
          onClick={handleLogin}
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70 disabled:transform-none"
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            fontSize: '1rem',
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            background: AUTH.buttonGradient,
            boxShadow: AUTH.buttonShadow,
          }}
        >
          <LogIn size={20} strokeWidth={2} />
          Sign in
        </button>

        <p
          style={{
            margin: '12px 0 0',
            color: AUTH.caption,
            fontSize: '0.75rem',
            lineHeight: 1.5,
          }}
        >
          You will be redirected to Microsoft for secure authentication
        </p>
      </div>

      <div
        className="flex w-full items-center justify-center"
        style={{
          marginTop: 8,
          padding: 16,
          borderRadius: 20,
          border: `1px solid ${AUTH.footnoteBorder}`,
          backgroundColor: AUTH.footnoteBg,
          color: AUTH.subtitle,
        }}
      >
        <span style={{ fontSize: '0.75rem' }}>
          Your credentials are securely managed by Microsoft
        </span>
      </div>
    </AuthCardShell>
  );
};

export default LoginPage;
